import { memo, useCallback } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../../context/CartContext";

// ─── SVG Icons ────────────────────────────────────────────────────────────────
const MinusIcon = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
  >
    <path d="M2 5h6" />
  </svg>
);

const PlusIcon = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
  >
    <path d="M5 2v6M2 5h6" />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 13 13"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="1 3 12 3" />
    <path d="M4.5 3V2a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1" />
    <path d="M2 3l.7 8.3A1 1 0 0 0 3.7 12h5.6a1 1 0 0 0 1-.7L11 3" />
  </svg>
);

// ─── CartItems ────────────────────────────────────────────────────────
function CartItems({ item }) {
  const { updateQuantity, removeItem } = useCart();

  const {
    _id,
    thumbnail: image,
    slug,
    title,
    variantName,
    quantity,
    price,
    mrp,
  } = item;

  const numPrice = Number(price);
  const numMrp = Number(mrp);

  const handleRemove = useCallback(() => {
    removeItem(_id);
  }, [removeItem, _id]);

  const handleQtyChange = useCallback(
    (delta) => {
      const newQty = quantity + delta;
      if (newQty < 1) return;
      updateQuantity(_id, newQty);
    },
    [updateQuantity, _id, quantity],
  );

  return (
    <div className="cart-item">
      {/* Thumbnail */}
      <div className="cart-item-thumb">
        {image ? (
          <img src={image} alt={title || slug} loading="lazy" />
        ) : (
          <div className="cart-item-thumb-placeholder" aria-hidden="true" />
        )}
      </div>

      {/* Details */}
      <div className="cart-item-details">
        <div className="cart-item-top">
          <div className="cart-item-info">
            {variantName && <p className="cart-item-craft">{variantName}</p>}
            <Link
              to={`/shop/${slug}`}
              className="cart-item-name"
              style={{ textDecoration: "none" }}
            >
              {title || slug}
            </Link>
          </div>
          <button
            className="cart-item-remove"
            onClick={handleRemove}
            aria-label={`Remove ${title || slug} from cart`}
            type="button"
          >
            <TrashIcon />
          </button>
        </div>

        <div className="cart-item-bottom">
          <div>
            <span className="cart-item-price">
              ₹{numPrice.toLocaleString("en-IN")}
            </span>
            {!isNaN(numMrp) && numMrp > numPrice && (
              <span className="cart-item-price-original">
                ₹{numMrp.toLocaleString("en-IN")}
              </span>
            )}
          </div>

          <div
            className="cart-qty"
            role="group"
            aria-label={`Quantity for ${title || slug}`}
          >
            <button
              className="cart-qty-btn"
              onClick={() => handleQtyChange(-1)}
              disabled={quantity <= 1}
              aria-label="Decrease quantity"
              type="button"
            >
              <MinusIcon />
            </button>
            <span className="cart-qty-value" aria-live="polite">
              {quantity}
            </span>
            <button
              className="cart-qty-btn"
              onClick={() => handleQtyChange(+1)}
              aria-label="Increase quantity"
              type="button"
            >
              <PlusIcon />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default memo(CartItems);
